import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';

const RentalReportLayer = () => {
    const [rentals, setRentals] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRental, setSelectedRental] = useState('');

    useEffect(() => {
        fetchRentals();
        fetchPayments();
    }, []);

    const fetchRentals = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            if (!apiUrl || !token) {
                throw new Error('Missing API URL or authentication token');
            }

            const response = await fetch(`${apiUrl}/rentals`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch rentals: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setRentals(data.data);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const fetchPayments = async () => {
        try {
            setLoading(true);
            setError('');

            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            if (!apiUrl || !token) {
                throw new Error('Missing API URL or authentication token');
            }

            const response = await fetch(`${apiUrl}/rental-payments`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch payments: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setPayments(data.data);
            } else {
                throw new Error(data.message || 'Failed to fetch payments');
            }
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setLoading(false);
        }
    };

    const filteredData = payments.filter(payment => {
        const matchesSearch = payment.rental && (
            payment.rental.tenant_names.toLowerCase().includes(searchQuery.toLowerCase()) ||
            payment.payment_month.toLowerCase().includes(searchQuery.toLowerCase())
        );
        
        const matchesRental = selectedRental === '' || payment.rental_id === parseInt(selectedRental);
        
        return matchesSearch && matchesRental;
    });

    const handleExportExcel = () => {
        // Prepare data for Excel
        const excelData = filteredData.map((payment, index) => ({
            'S.No': index + 1,
            'Tenant Name': payment.rental ? payment.rental.tenant_names : 'N/A',
            'S/N': payment.rental ? payment.rental.sn : 'N/A',
            'District': payment.rental ? payment.rental.district : 'N/A',
            'Current Rent': payment.rental ? payment.rental.current_rent_per_month : '0',
            'Payment Month': payment.payment_month,
            'Amount Paid': payment.amount_paid,
            'Payment Date': payment.payment_date ? new Date(payment.payment_date).toISOString().split('T')[0] : 'N/A',
            'Notes': payment.notes || '-'
        }));

        // Create workbook and worksheet
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Rental Report');

        // Set column widths
        const columnWidths = [
            { wch: 8 },  // S.No
            { wch: 30 }, // Tenant Name
            { wch: 20 }, // S/N
            { wch: 15 }, // District
            { wch: 15 }, // Current Rent
            { wch: 15 }, // Payment Month
            { wch: 15 }, // Amount Paid
            { wch: 15 }, // Payment Date
            { wch: 30 }  // Notes
        ];
        worksheet['!cols'] = columnWidths;

        // Generate Excel file and trigger download
        XLSX.writeFile(workbook, `Rental_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    // Calculate statistics
    const totalPayments = filteredData.reduce((sum, payment) => sum + parseFloat(payment.amount_paid || 0), 0);
    const totalRentals = rentals.length;
    const totalExpectedRent = rentals.reduce((sum, rental) => sum + parseFloat(rental.current_rent_per_month || 0), 0);

    return (
        <>
            {/* Statistics Cards */}
            <div className='row gy-4 mb-24'>
                <div className='col-xxl-4 col-sm-6'>
                    <div className='card p-3 shadow-sm radius-12 h-100'>
                        <div className='card-body p-0'>
                            <div className='d-flex flex-wrap align-items-center justify-content-between gap-1'>
                                <div>
                                    <span className='mb-2 fw-medium text-secondary-light text-sm'>
                                        Total Rentals
                                    </span>
                                    <h6 className='fw-semibold my-1'>{totalRentals}</h6>
                                </div>
                                <div className='w-64-px h-64-px radius-12 bg-primary-600 d-flex justify-content-center align-items-center'>
                                    <Icon
                                        icon='mdi:home-account'
                                        className='text-white text-32'
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='col-xxl-4 col-sm-6'>
                    <div className='card p-3 shadow-sm radius-12 h-100'>
                        <div className='card-body p-0'>
                            <div className='d-flex flex-wrap align-items-center justify-content-between gap-1'>
                                <div>
                                    <span className='mb-2 fw-medium text-secondary-light text-sm'>
                                        Total Expected Rent
                                    </span>
                                    <h6 className='fw-semibold my-1'>${totalExpectedRent.toFixed(2)}</h6>
                                </div>
                                <div className='w-64-px h-64-px radius-12 bg-info-600 d-flex justify-content-center align-items-center'>
                                    <Icon
                                        icon='mdi:cash-multiple'
                                        className='text-white text-32'
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='col-xxl-4 col-sm-6'>
                    <div className='card p-3 shadow-sm radius-12 h-100'>
                        <div className='card-body p-0'>
                            <div className='d-flex flex-wrap align-items-center justify-content-between gap-1'>
                                <div>
                                    <span className='mb-2 fw-medium text-secondary-light text-sm'>
                                        Total Payments Collected
                                    </span>
                                    <h6 className='fw-semibold my-1'>${totalPayments.toFixed(2)}</h6>
                                </div>
                                <div className='w-64-px h-64-px radius-12 bg-success-600 d-flex justify-content-center align-items-center'>
                                    <Icon
                                        icon='mdi:cash-check'
                                        className='text-white text-32'
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Report Table */}
            <div className='card h-100 p-0 radius-12'>
                <div className='card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between'>
                    <div className='d-flex align-items-center flex-wrap gap-3'>
                        <form className='navbar-search'>
                            <input
                                type='text'
                                className='bg-base h-40-px w-auto'
                                name='search'
                                placeholder='Search'
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <Icon icon='ion:search-outline' className='icon' />
                        </form>
                        <select
                            className='form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px'
                            value={selectedRental}
                            onChange={(e) => setSelectedRental(e.target.value)}
                        >
                            <option value=''>All Rentals</option>
                            {rentals.map(rental => (
                                <option key={rental.id} value={rental.id}>
                                    {rental.tenant_names}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        type='button'
                        className='btn btn-success text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2'
                        onClick={handleExportExcel}
                        disabled={filteredData.length === 0}
                    >
                        <Icon
                            icon='vscode-icons:file-type-excel'
                            className='icon text-xl line-height-1'
                        />
                        Export Excel
                    </button>
                </div>
                <div className='card-body p-24'>
                    {error && (
                        <div className='alert alert-danger mb-3' role='alert'>
                            {error}
                        </div>
                    )}

                    {loading ? (
                        <div className='text-center py-5'>
                            <div className='spinner-border text-primary-600' role='status'>
                                <span className='visually-hidden'>Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className='table-responsive scroll-sm'>
                            <table className='table bordered-table sm-table mb-0'>
                                <thead>
                                    <tr>
                                        <th scope='col'>S.No</th>
                                        <th scope='col'>Tenant Name</th>
                                        <th scope='col'>S/N</th>
                                        <th scope='col'>District</th>
                                        <th scope='col'>Current Rent</th>
                                        <th scope='col'>Payment Month</th>
                                        <th scope='col'>Amount Paid</th>
                                        <th scope='col'>Payment Date</th>
                                        <th scope='col'>Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredData.length === 0 ? (
                                        <tr>
                                            <td colSpan='9' className='text-center py-4'>
                                                No payment records found.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredData.map((payment, index) => (
                                            <tr key={payment.id}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <span className='text-md mb-0 fw-normal text-secondary-light'>
                                                        {payment.rental ? payment.rental.tenant_names : 'N/A'}
                                                    </span>
                                                </td>
                                                <td>{payment.rental ? payment.rental.sn : 'N/A'}</td>
                                                <td>{payment.rental ? payment.rental.district : 'N/A'}</td>
                                                <td>${payment.rental ? payment.rental.current_rent_per_month : '0'}</td>
                                                <td>{payment.payment_month}</td>
                                                <td className='fw-semibold'>${payment.amount_paid}</td>
                                                <td>{payment.payment_date ? new Date(payment.payment_date).toLocaleDateString() : 'N/A'}</td>
                                                <td>{payment.notes || '-'}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default RentalReportLayer;

